import * as React from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbDivider,
  BreadcrumbButton,
  Text,
  Button,
  Link,
  MenuProps,
  Menu,
  MenuPopover,
  MenuList,
  MenuItem,
  PositioningImperativeRef,
  useRestoreFocusTarget,
} from "@fluentui/react-components";
import { ChevronLeftRegular, ChevronLeftFilled, bundleIcon } from "@fluentui/react-icons";

import { useIconButtonStyles, usePageStyles } from "../styles/styles";
import { FUNC_NAMESPACE, CUSTOM_FUNCTION_USAGE_EXAMPLES } from "../../functions/functions-extra-metadata";

import { CUSTOM_FUNCTION_METADATA, CUSTOM_FUNCTION_NAMES } from "../../functions/functions-metadata";

import { PATHS } from "../components/routes/paths";
import { insertCustomFunctionEgToCell } from "../../helpers/utils";

const BackIcon = bundleIcon(ChevronLeftFilled, ChevronLeftRegular);

const CustomBreadcrumbs = ({ parentPath }) => {
  const params = useParams();
  const history = useHistory();
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const customAnchorRef = React.useRef<HTMLButtonElement>(null);
  const positioningRef = React.useRef<PositioningImperativeRef>(null);
  const [open, setOpen] = React.useState(false);

  const onOpenChange: MenuProps["onOpenChange"] = (e, data) => {
    // do not close menu as an outside click if clicking on the custom trigger/target
    // this prevents it from closing & immediately re-opening when clicking custom triggers
    if (data.type === "clickOutside" && (e.target === customAnchorRef.current || e.target === buttonRef.current)) {
      return;
    }

    setOpen(data.open);
  };

  React.useEffect(() => {
    if (buttonRef.current) {
      positioningRef.current?.setTarget(buttonRef.current);
    }
  }, [buttonRef, positioningRef]);

  const restoreFocusTargetAttribute = useRestoreFocusTarget();

  return (
    <div>
      <Breadcrumb size="small" aria-label="Breadcrumb default example">
        <BreadcrumbItem>
          <BreadcrumbButton
            onClick={() => {
              history.push(parentPath);
            }}
          >
            docs
          </BreadcrumbButton>
        </BreadcrumbItem>
        <BreadcrumbDivider />
        <BreadcrumbItem>
          <BreadcrumbButton {...restoreFocusTargetAttribute} ref={buttonRef} onClick={() => setOpen((s) => !s)}>
            {params.funcName.toLowerCase()}
          </BreadcrumbButton>
        </BreadcrumbItem>
      </Breadcrumb>

      <Menu open={open} onOpenChange={onOpenChange} positioning={{ positioningRef }}>
        <MenuPopover>
          <MenuList>
            {CUSTOM_FUNCTION_NAMES.map((funcName, idx) => (
              <MenuItem key={idx} onClick={() => history.push(`${PATHS.DOCS_PAGE}/${funcName}`)}>
                {funcName}
              </MenuItem>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
};

const BoldText = ({ children }) => (
  <Text as="h1" size={500} weight="bold" truncate block>
    {children}
  </Text>
);

const SemiboldText = ({ children }) => (
  <Text as="h2" size={400} weight="semibold" block>
    {children}
  </Text>
);

export default function FuncDocPage() {
  const params = useParams();
  const history = useHistory();
  const pageStyles = usePageStyles();
  const iconStyles = useIconButtonStyles();

  const funcName: string = params.funcName;
  const funcFullName = `${FUNC_NAMESPACE}.${funcName}`;
  const funcMetadata = CUSTOM_FUNCTION_METADATA[funcName];
  const funcDescription = funcMetadata["description"];
  const funcParams = funcMetadata["parameters"];
  const funcParamNames = funcParams
    .map((paramMetadata) => (paramMetadata.optional ? `[${paramMetadata.name}]` : paramMetadata.name))
    .join(", ");
  const funcUsageExamples = CUSTOM_FUNCTION_USAGE_EXAMPLES[funcName];

  const parentPath = PATHS.DOCS_PAGE;

  return (
    <div className={pageStyles.container}>
      <Button
        className={iconStyles.dark}
        style={{
          position: "fixed",
          top: "4rem",
          right: "0.75rem",
        }}
        icon={<BackIcon />}
        appearance="subtle"
        onClick={() => history.push(parentPath)}
      >
        Back
      </Button>

      <CustomBreadcrumbs parentPath={parentPath} />

      <BoldText>{funcFullName}</BoldText>

      <div>
        <SemiboldText>Description</SemiboldText>

        <Text as="p" size={300}>
          {funcDescription}
        </Text>
      </div>

      <div>
        <SemiboldText>Syntax</SemiboldText>

        <Text as="p" font="monospace" size={300}>
          {funcFullName}({funcParamNames})
        </Text>
      </div>

      <div>
        <SemiboldText>Examples</SemiboldText>

        <div className="flex flex-col gap-[0.75rem]">
          {funcUsageExamples.map((funcEg, idx) => (
            <Link key={idx} as="button" appearance="subtle" onClick={() => insertCustomFunctionEgToCell(funcEg)}>
              <Text as="p" font="monospace" size={300}>
                {funcEg}
              </Text>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
